import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';

import { PiiCell, requestClassification } from '@/features/catalog-browser';
import type { CatalogRow } from '@/shared/api/generated/catalog';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { BrandLoader } from '@/shared/ui/BrandLoader';
import { Button } from '@/shared/ui/Button';
import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

import {
  SOURCE_DATASETS_KEY,
  useProfileJobs,
  useRequestProfiles,
  useSourceDatasets,
} from '../api/sourceDatasets';
import { ageSince } from '../model/listing';
import {
  DATASET_FILTERS,
  hasProfile,
  isFinished,
  jobSignature,
  matchesFilter,
  nameMatches,
  profilable,
  profileRunItemOf,
  profileStatusOf,
  qualifiedName,
} from '../model/sourceDatasets';
import type {
  ClassifyRunState,
  DatasetFilter,
  ProfileRunState,
  ProfileStatus,
} from '../model/sourceDatasets';
import { BulkRunDialog } from './BulkRunDialog';
import type { BulkRun } from './BulkRunDialog';

/* A source's datasets, as the design's Datasets tab: search, three filter chips, row checkboxes, a
 * dark selection bar with Run profile and Classify PII, and the same two for the whole source.
 *
 * **Profiling is queued; classification runs from here.** A profile aggregates a customer's rows, so
 * "Run profile" goes through the source's profile queue, which paces it by the source's own load
 * bound and survives leaving the page — every row shows where its job is. A classification reads
 * metadata the platform already holds, so it runs as per-dataset requests from this page, five at a
 * time, with each row's outcome shown as it lands.
 *
 * **The whole source is walked, bounded** (the catalogue browser's own walk), so the chips count the
 * source rather than a page and "all" means all. The table pages fifty at a time on screen only.
 */

const PAGE = 50;
const CLASSIFY_CONCURRENCY = 5;

const PROFILE_TONE = {
  running: 'info',
  queued: 'info',
  retrying: 'warn',
  failed: 'danger',
  profiled: 'ok',
  never: 'neutral',
  unknown: 'unknown',
} as const satisfies Record<ProfileStatus['kind'], BadgeTone>;

function ProfileCell({ status }: { readonly status: ProfileStatus }): ReactElement {
  const { t } = useTranslation();
  const age = status.kind === 'profiled' ? ageSince(status.at, new Date()) : null;
  return (
    <span className="lq-sd__profile">
      <StatusBadge
        tone={PROFILE_TONE[status.kind]}
        label={t(`sourceDatasets.profile.${status.kind}`)}
        {...(status.kind === 'failed' && status.detail !== null
          ? { title: status.detail }
          : {})}
      />
      {age === null || age.kind === 'never' ? null : (
        <span className="lq-sd__age">
          {age.kind === 'justNow'
            ? t('sourceDatasets.age.justNow')
            : t(`sourceDatasets.age.${age.kind}`, { count: age.count })}
        </span>
      )}
    </span>
  );
}

export function SourceDatasetsTab({
  connectionId,
  connectionName,
  inService,
  mayProfile,
  mayClassify,
}: {
  readonly connectionId: string;
  readonly connectionName: string;
  readonly inService: boolean;
  readonly mayProfile: boolean;
  readonly mayClassify: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const client = useQueryClient();
  const datasets = useSourceDatasets(connectionId);
  const rows = useMemo(() => datasets.data?.rows ?? [], [datasets.data]);
  const ids = useMemo(() => rows.map((row) => row.id), [rows]);
  const jobs = useProfileJobs(connectionId, ids);
  const profiles = useRequestProfiles();

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<DatasetFilter>('all');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [run, setRun] = useState<BulkRun | null>(null);

  const factsRead = datasets.data?.factsRead ?? false;
  const facts = datasets.data?.facts;
  const statusOf = (row: CatalogRow): ProfileStatus =>
    profileStatusOf(facts?.get(row.id), factsRead, jobs.data?.get(row.id));
  const profiledOf = (row: CatalogRow): boolean | null =>
    hasProfile(facts?.get(row.id), factsRead);

  const searched = rows.filter((row) => nameMatches(row, query));
  const counts = Object.fromEntries(
    DATASET_FILTERS.map((each) => [
      each,
      searched.filter((row) => matchesFilter(each, row, profiledOf(row))).length,
    ]),
  ) as Record<DatasetFilter, number>;
  const shown = searched.filter((row) => matchesFilter(filter, row, profiledOf(row)));
  const pages = Math.max(1, Math.ceil(shown.length / PAGE));
  const current = Math.min(page, pages - 1);
  const visible = shown.slice(current * PAGE, current * PAGE + PAGE);

  const selectedShown = shown.filter((row) => selected.has(row.id));
  const allShownSelected = shown.length > 0 && selectedShown.length === shown.length;
  const someShownSelected = selectedShown.length > 0 && !allShownSelected;
  const selectAll = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAll.current !== null) selectAll.current.indeterminate = someShownSelected;
  }, [someShownSelected]);

  /* ------------------------------------------------------------------ the bulk runs */

  const names = (targets: readonly CatalogRow[]): ReadonlyMap<string, string> =>
    new Map(targets.map((row) => [row.id, qualifiedName(row)]));

  const plan = (
    kind: BulkRun['kind'],
    targets: readonly CatalogRow[],
    all: boolean,
  ): void => {
    const eligible = kind === 'profile' ? targets.filter(profilable) : targets;
    if (eligible.length === 0) return;
    setRun({
      kind,
      all,
      ids: eligible.map((row) => row.id),
      names: names(eligible),
      stage: 'plan',
      hidden: false,
      startedAt: null,
      queued: null,
      classified: {},
      baseline: {},
      error: null,
    });
  };

  const startProfile = (current: BulkRun): void => {
    const startedAt = new Date().toISOString();
    const baseline = Object.fromEntries(
      current.ids.map((id) => [id, jobSignature(jobs.data?.get(id))]),
    );
    profiles.mutate(
      { connectionId, datasetIds: current.all ? undefined : current.ids },
      {
        onSuccess: (outcome) => {
          setRun((was) =>
            was === null
              ? was
              : { ...was, stage: 'run', startedAt, baseline, queued: outcome.queued },
          );
          setSelected(new Set());
        },
        onError: (error) => {
          setRun((was) => (was === null ? was : { ...was, error: error.message }));
        },
      },
    );
  };

  const startClassify = (current: BulkRun): void => {
    setRun((was) =>
      was === null
        ? was
        : {
            ...was,
            stage: 'run',
            startedAt: new Date().toISOString(),
            classified: Object.fromEntries(was.ids.map((id) => [id, 'queued'])),
          },
    );
    setSelected(new Set());
    const queue = [...current.ids];
    const mark = (id: string, state: ClassifyRunState): void => {
      setRun((was) =>
        was === null ? was : { ...was, classified: { ...was.classified, [id]: state } },
      );
    };
    const worker = async (): Promise<void> => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        mark(id, 'running');
        try {
          const outcome = await requestClassification(id);
          mark(id, outcome.verdicts.length > 0 ? 'review' : 'clean');
        } catch {
          mark(id, 'failed');
        }
      }
    };
    void Promise.all(
      Array.from({ length: Math.min(CLASSIFY_CONCURRENCY, queue.length) }, () =>
        worker(),
      ),
    ).then(() => {
      void client.invalidateQueries({ queryKey: [...SOURCE_DATASETS_KEY, connectionId] });
      void client.invalidateQueries({ queryKey: ['catalog'] });
    });
  };

  /* The run's per-table states: the queue's answer for a profile (a finished job unchanged since
     the run started is not this run's, so it reads as still queued), the run's own record for a
     classification. */
  const runStates = (
    current: BulkRun,
  ): Readonly<Record<string, ProfileRunState | ClassifyRunState>> => {
    if (current.kind === 'classify') return current.classified;
    return Object.fromEntries(
      current.ids.map((id) => [
        id,
        profileRunItemOf(jobs.data?.get(id), current.baseline[id]),
      ]),
    );
  };
  const states = run === null ? {} : runStates(run);
  const finished =
    run !== null &&
    run.stage === 'run' &&
    run.ids.every((id) => {
      const state = states[id];
      return state !== undefined && isFinished(state);
    });
  const done =
    run === null
      ? 0
      : run.ids.filter((id) => {
          const state = states[id];
          return state !== undefined && isFinished(state);
        }).length;

  /* A profile run's finish is when the facts are worth re-reading: the "Profiled · just now" a
     reader is waiting for lives there, not in the queue. */
  const refreshed = useRef<string | null>(null);
  useEffect(() => {
    if (run?.kind === 'profile' && finished && refreshed.current !== run.startedAt) {
      refreshed.current = run.startedAt;
      void client.invalidateQueries({ queryKey: [...SOURCE_DATASETS_KEY, connectionId] });
    }
  }, [run, finished, client, connectionId]);

  const gateTitle = (may: boolean, missing: string): string | undefined =>
    !inService ? t('sourceDatasets.outOfService') : !may ? t(missing) : undefined;

  /* ------------------------------------------------------------------ render */

  if (datasets.isPending) {
    return (
      <div className="lq-sd" aria-busy="true">
        <p className="lq-sd__note">{t('sourceDatasets.loading')}</p>
        <SkeletonRows rows={5} columns={4} />
      </div>
    );
  }

  if (datasets.isError) {
    return (
      <Alert tone="error" title={t('sourceDatasets.unreadable')}>
        {datasets.error.message}
      </Alert>
    );
  }

  const runningBanner = run !== null && run.hidden && run.stage === 'run' && !finished;

  return (
    <div className="lq-sd">
      {runningBanner ? (
        <div className="lq-sd__banner" role="status">
          <BrandLoader size="sm" />
          <span className="lq-sd__bannerText">
            {t(`sourceDatasets.banner.${run.kind}`, { done, total: run.ids.length })}
          </span>
          <span className="lq-sd__bannerTrack" aria-hidden="true">
            <span
              className="lq-sd__bannerFill"
              style={{
                transform: `scaleX(${String(run.ids.length === 0 ? 0 : done / run.ids.length)})`,
              }}
            />
          </span>
          <Button
            size="compact"
            onClick={() => {
              setRun({ ...run, hidden: false });
            }}
          >
            {t('sourceDatasets.banner.view')}
          </Button>
        </div>
      ) : null}

      <div className="lq-sd__bar">
        <input
          className="lq-input lq-sd__search"
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setPage(0);
          }}
          placeholder={t('sourceDatasets.search')}
          aria-label={t('sourceDatasets.search')}
        />
        <SegmentedFilter
          label={t('sourceDatasets.filterLabel')}
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(0);
            setSelected(new Set());
          }}
          options={DATASET_FILTERS.map((each) => ({
            id: each,
            label: t(`sourceDatasets.filter.${each}`),
            count: counts[each],
          }))}
        />
        <span className="lq-sd__spacer" />
        {/* The design's rule: with a selection made, the whole-source actions give way to the bar
            below, so "Profile all" is never one click away from somebody who meant their three. */}
        {selected.size === 0 ? (
          <>
            <Button
              size="compact"
              disabled={!mayProfile || !inService || rows.length === 0}
              title={gateTitle(mayProfile, 'sourceDatasets.needsProfile')}
              onClick={() => {
                plan('profile', rows, true);
              }}
            >
              {t('sourceDatasets.profileAll')}
            </Button>
            <Button
              size="compact"
              disabled={!mayClassify || rows.length === 0}
              title={mayClassify ? undefined : t('sourceDatasets.needsClassify')}
              onClick={() => {
                plan('classify', rows, true);
              }}
            >
              {t('sourceDatasets.classifyAll')}
            </Button>
          </>
        ) : null}
      </div>

      {selected.size > 0 ? (
        <div
          className="lq-sd__selection"
          role="region"
          aria-label={t('sourceDatasets.selectionLabel')}
        >
          <span className="lq-sd__selectionCount">
            {t('sourceDatasets.selected', { count: selected.size })}
          </span>
          <Button
            size="compact"
            disabled={!mayProfile || !inService}
            title={gateTitle(mayProfile, 'sourceDatasets.needsProfile')}
            onClick={() => {
              plan(
                'profile',
                rows.filter((row) => selected.has(row.id)),
                false,
              );
            }}
          >
            {t('sourceDatasets.runProfile')}
          </Button>
          <Button
            size="compact"
            disabled={!mayClassify}
            title={mayClassify ? undefined : t('sourceDatasets.needsClassify')}
            onClick={() => {
              plan(
                'classify',
                rows.filter((row) => selected.has(row.id)),
                false,
              );
            }}
          >
            {t('sourceDatasets.classifyPii')}
          </Button>
          <button
            type="button"
            className="lq-sd__clear"
            onClick={() => {
              setSelected(new Set());
            }}
          >
            {t('sourceDatasets.clear')}
          </button>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="lq-sd__empty">{t('sourceDatasets.emptySource')}</p>
      ) : shown.length === 0 ? (
        <p className="lq-sd__empty">{t('sourceDatasets.emptyFilter')}</p>
      ) : (
        <div className="lq-table-scroll lq-table-scroll--framed lq-sd__frame">
          <table className="lq-table lq-sd__table">
            <caption className="lq-visually-hidden">
              {t('sourceDatasets.caption')}
            </caption>
            <thead>
              <tr>
                <th scope="col" className="lq-sd__pick">
                  <label className="lq-sd__hit">
                    <input
                      ref={selectAll}
                      type="checkbox"
                      className="lq-sd__check"
                      checked={allShownSelected}
                      onChange={() => {
                        setSelected(
                          allShownSelected
                            ? new Set()
                            : new Set(shown.map((row) => row.id)),
                        );
                      }}
                      aria-label={t('sourceDatasets.selectAll', { count: shown.length })}
                    />
                  </label>
                </th>
                <th scope="col">{t('sourceDatasets.column.dataset')}</th>
                <th scope="col">{t('sourceDatasets.column.profile')}</th>
                <th scope="col">{t('sourceDatasets.column.pii')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const name = qualifiedName(row);
                return (
                  <tr
                    key={row.id}
                    data-selected={selected.has(row.id) ? 'true' : undefined}
                  >
                    <td className="lq-sd__pick" data-label="">
                      <label className="lq-sd__hit">
                        <input
                          type="checkbox"
                          className="lq-sd__check"
                          checked={selected.has(row.id)}
                          onChange={() => {
                            setSelected((was) => {
                              const next = new Set(was);
                              if (next.has(row.id)) next.delete(row.id);
                              else next.add(row.id);
                              return next;
                            });
                          }}
                          aria-label={t('sourceDatasets.select', { name })}
                        />
                      </label>
                    </td>
                    <td data-label={t('sourceDatasets.column.dataset')}>
                      <DatasetObjectTypeIcon objectType={row.object_type} />
                      <Link
                        className="lq-sd__name"
                        to={generatePath(SCREEN_PATHS.dataset, { datasetId: row.id })}
                      >
                        <span className="lq-sd__schema">{row.schema_name}.</span>
                        {row.object_name}
                      </Link>
                      {row.state === 'vanished' ? (
                        <span className="lq-sd__vanished">
                          {t('sourceDatasets.vanished')}
                        </span>
                      ) : null}
                    </td>
                    <td data-label={t('sourceDatasets.column.profile')}>
                      <ProfileCell status={statusOf(row)} />
                    </td>
                    <td data-label={t('sourceDatasets.column.pii')}>
                      <PiiCell row={row} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {shown.length > PAGE ? (
        <div className="lq-sd__pager">
          <span className="lq-sd__note">
            {t('sourceDatasets.range', {
              from: current * PAGE + 1,
              to: Math.min(shown.length, current * PAGE + PAGE),
              total: shown.length,
            })}
          </span>
          <Button
            size="compact"
            disabled={current === 0}
            onClick={() => {
              setPage(current - 1);
            }}
          >
            {t('sourceDatasets.previous')}
          </Button>
          <Button
            size="compact"
            disabled={current >= pages - 1}
            onClick={() => {
              setPage(current + 1);
            }}
          >
            {t('sourceDatasets.next')}
          </Button>
        </div>
      ) : null}

      {datasets.data.truncated ? (
        <p className="lq-sd__note">
          {t('sourceDatasets.truncated', { count: rows.length })}
        </p>
      ) : null}
      {!factsRead ? (
        <p className="lq-sd__note">{t('sourceDatasets.factsUnread')}</p>
      ) : null}

      {run !== null ? (
        <BulkRunDialog
          run={run}
          connectionName={connectionName}
          states={states}
          finished={finished}
          busy={profiles.isPending}
          onStart={() => {
            if (run.kind === 'profile') startProfile(run);
            else startClassify(run);
          }}
          onHide={() => {
            setRun({ ...run, hidden: true });
          }}
          onClose={() => {
            setRun(null);
          }}
        />
      ) : null}
    </div>
  );
}
